import { useMyMediaContext, UnassignedFile } from "../context/MyMediaContext";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Button } from "@/modern/components/ui/button";
import { Trash2, Loader2, FileText, Sparkles } from "lucide-react";
import { useState, useMemo } from "react";
import { cn } from "@/modern/utils";
import FilePreview from "@/modern/components/file-preview";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { getFormattedFileSize } from "@/other/utils/utils";
import { DateHelper } from "@bryntum/schedulerpro";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/modern/components/ui/tooltip";

// Helper function to convert UnassignedFile to SwivuFile
const convertToSwivuFile = (file: UnassignedFile): SwivuFile => {
  return {
    id: file.id,
    name: file.name || `file-${file.id}`,
    thumbnailUrl: file.thumbnail_url || null,
    url: file.url || "",
    task: null,
    resource: null,
    client: null,
    location: null,
    document: null,
    board: 0,
    order: 0,
    description: null,
    createdAt: file.created_at || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    creator: null,
    updater: null,
    sizeBytes: typeof file.size === "number" ? file.size : null,
    fileType: "image",
    source: "swivu",
    creatorName: null,
    updaterName: null,
    hasPermission: null,
    canAnalyze: null,
    externalId: null,
    canViewGrant: 0,
    canEditGrant: 0,
    isAnnotated: false,
  };
};

export const FileGrid = () => {
  const {
    files,
    selectedFiles,
    toggleFileSelection,
    deleteUnassignedFileById,
    isLoading,
    entityRecord,
    searchQuery,
    viewMode,
    beginInsightFlow,
  } = useMyMediaContext();
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const canSelect = entityRecord !== null;

  // Memoize formatted files to prevent creating new objects on every render
  const formattedFilesMap = useMemo(() => {
    const map = new Map<number, SwivuFile>();
    files.forEach((file) => {
      map.set(file.id, convertToSwivuFile(file));
    });
    return map;
  }, [files]);

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deletingIds.has(id)) return;

    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deleteUnassignedFileById(id);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-20">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mb-6 shadow-lg">
          <FileText className="h-10 w-10 text-primary" />
        </div>
        <h3 className="text-foreground text-xl font-semibold mb-2">No files found</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {searchQuery ? (
            <>
              No files match your search query. <br />
              Try adjusting your search terms.
            </>
          ) : (
            <>
              You haven't uploaded any files yet. <br />
              Click "Upload Files" to get started.
            </>
          )}
        </p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div className="space-y-2">
        {files.map((file) => {
          const isSelected = selectedFiles.has(file.id);
          const isDeleting = deletingIds.has(file.id);
          const formattedFile = formattedFilesMap.get(file.id)!;
          const hasInsights = (file.insight_count ?? 0) > 0;

          return (
            <div
              key={file.id}
              className={cn(
                "flex items-center gap-4 p-3 rounded-lg border transition-all duration-200 group",
                canSelect && "cursor-pointer",
                isSelected
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border bg-card hover:border-primary/50 hover:bg-accent/50"
              )}
              onClick={canSelect ? () => toggleFileSelection(file.id) : undefined}
            >
              {/* Checkbox */}
              {canSelect && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleFileSelection(file.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              {/* Thumbnail */}
              <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-muted/50">
                <FilePreview
                  file={formattedFile}
                  src={file.thumbnail_url || file.url}
                  className="w-full h-full"
                  disableLoadingState={true}
                  fileIconContainerClassName="bg-transparent"
                  fileIconClassName="size-8"
                  enableVideoAudioRendering={true}
                  isSwivuUnAssignedFile={true}
                  isThumbnail={true}
                />
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-sm font-medium truncate",
                    isSelected && "text-primary"
                  )}
                  title={formattedFile.name}
                >
                  {formattedFile.name}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  {file.size && (
                    <span>{getFormattedFileSize(file.size)}</span>
                  )}
                  {file.size && file.created_at && <span>•</span>}
                  {file.created_at && (
                    <span>
                      {DateHelper.format(new Date(file.created_at), "MM/DD/YYYY")}
                    </span>
                  )}
                </div>
              </div>

              {/* Insight generation button */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-8 w-8", hasInsights && "text-primary hover:text-primary")}
                        onClick={(e) => { e.stopPropagation(); beginInsightFlow(file); }}
                      >
                        <Sparkles className={cn("h-4 w-4", hasInsights && "text-primary")} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>AI Insights</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Delete button */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => handleDelete(file.id, e)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {files.map((file) => {
        const isSelected = selectedFiles.has(file.id);
        const isDeleting = deletingIds.has(file.id);
        const formattedFile = formattedFilesMap.get(file.id)!;
        const hasInsights = (file.insight_count ?? 0) > 0;

        return (
          <div
            key={file.id}
            className={cn(
              "relative group aspect-square rounded-xl border overflow-hidden transition-all duration-200",
              canSelect && "cursor-pointer",
              isSelected
                ? "border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/30"
                : "border-border/50 bg-card hover:border-primary/50 hover:shadow-md hover:shadow-primary/5"
            )}
            onClick={canSelect ? () => toggleFileSelection(file.id) : undefined}
          >
            {canSelect && (
              <div className="absolute top-3 left-3 z-20">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleFileSelection(file.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            <div className="absolute top-3 right-3 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant={hasInsights ? "default" : "secondary"}
                size="icon"
                className="h-8 w-8 shadow-md"
                onClick={(e) => { e.stopPropagation(); beginInsightFlow(file); }}
              >
                <Sparkles className="h-4 w-4" />
              </Button>
              <Button
                variant="destructive"
                size="icon"
                className="h-8 w-8 shadow-md"
                onClick={(e) => handleDelete(file.id, e)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* File Preview */}
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/20 to-muted/40">
              <FilePreview
                file={formattedFile}
                src={file.thumbnail_url || file.url}
                className="w-full h-full"
                disableLoadingState={true}
                fileIconContainerClassName="bg-transparent"
                fileIconClassName="size-10"
                isSwivuUnAssignedFile={true}
                isThumbnail={true}
              />

              <span className="text-xs text-center px-2 break-words max-w-full absolute bottom-1 left-1 right-1 border/50 bg-background/50 rounded-full truncate">{formattedFile.name}</span>
            </div>

            {/* Selection overlay */}
            {isSelected && (
              <div className="absolute inset-0 bg-primary/5 pointer-events-none rounded-xl" />
            )}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-xl" />
          </div>
        );
      })}
    </div>
  );
};
